import React from "react";
import { type ClientApp, TableSourceJS } from "target_wasm";
import { DatasetUsageNote } from "@/components/main/datasets/DatasetUsageNote";
import { isImportedSource } from "components/canvas/utils/roles";
import {
  datasetUsageForTables,
  datasetUsageTotal,
  type DatasetUsage,
} from "@/lib/api/datasets";
import { askConfirm } from "stores/useConfirmStore";

export interface DeleteTarget {
  id: string;
  name: string;
  source: TableSourceJS;
}

/// Mirrors `UIJSEvent::DeleteTable`, which un-imports an import and purges a
/// recipe result. An import shares its source's table id, so asking about its
/// datasets would name the SOURCE's, which un-importing never touches.
const goesToTrash = (source: TableSourceJS): boolean =>
  !isImportedSource(source) && source !== TableSourceJS.RecipeResult;

/// Deletes tables, asking first only when one bound for the Trash is in a dataset.
export async function deleteTables(
  clientApp: ClientApp | null | undefined,
  targets: DeleteTarget[],
): Promise<void> {
  if (!clientApp || targets.length === 0) return;
  const dispatchDeletes = () => {
    for (const { id } of targets) clientApp.handle_delete_table_ui(id, false);
  };
  const trashed = targets.filter((t) => goesToTrash(t.source));
  // The Trash is undoable, so an unreadable count moves on rather than blocking.
  const byTable =
    trashed.length === 0
      ? {}
      : await datasetUsageForTables(trashed.map((t) => t.id)).catch(
          () => ({}) as Record<string, DatasetUsage>,
        );
  const inDatasets = trashed
    .map((t) => ({ ...t, usage: byTable[t.id] }))
    .filter((t) => datasetUsageTotal(t.usage) > 0);
  if (inDatasets.length === 0) {
    dispatchDeletes();
    return;
  }
  askConfirm({
    title: "Move to Trash",
    message: (
      <div data-testid="trash-table-confirm">
        {inDatasets.map((t) => (
          <div key={t.id}>
            {targets.length > 1 && (
              <p className="mt-2 text-fg-bold">{t.name}</p>
            )}
            <DatasetUsageNote usage={t.usage} />
          </div>
        ))}
      </div>
    ),
    confirmText: "Move to Trash",
    onConfirm: dispatchDeletes,
  });
}
